import React, { useState, useEffect } from 'react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../components/ui/popover';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import { GearSix, X } from '@phosphor-icons/react';
import { AIExtractionConfig } from '../../types/custom-field';
import { toast } from 'react-toastify';

interface AIExtractionConfigPopoverProps {
  trigger?: React.ReactNode;
  currentConfig: AIExtractionConfig | null;
  onSave: (config: AIExtractionConfig) => Promise<void>;
  isDisabled?: boolean;
}

const AIExtractionConfigPopover: React.FC<AIExtractionConfigPopoverProps> = ({
  trigger,
  currentConfig,
  onSave,
  isDisabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [fileLabelsInput, setFileLabelsInput] = useState('');
  const [fileLabels, setFileLabels] = useState<string[]>([]);
  const [extractionPrompt, setExtractionPrompt] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (currentConfig) {
      setEnabled(currentConfig.enabled);
      setFileLabels(currentConfig.fileLabels || []);
      setFileLabelsInput('');
      setExtractionPrompt(currentConfig.extractionPrompt || '');
    } else {
      setEnabled(false);
      setFileLabels([]);
      setFileLabelsInput('');
      setExtractionPrompt('');
    }
  }, [currentConfig, open]);

  const handleAddLabel = () => {
    const trimmedLabel = fileLabelsInput.trim();
    if (trimmedLabel && !fileLabels.includes(trimmedLabel)) {
      setFileLabels([...fileLabels, trimmedLabel]);
      setFileLabelsInput('');
    }
  };

  const handleRemoveLabel = (labelToRemove: string) => {
    setFileLabels(fileLabels.filter((label) => label !== labelToRemove));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddLabel();
    }
  };

  const handleSave = async () => {
    if (enabled && fileLabels.length === 0) {
      toast.error('Voeg minimaal één bestandslabel toe');
      return;
    }

    if (enabled && !extractionPrompt.trim()) {
      toast.error('Vul een extractie prompt in');
      return;
    }

    const config: AIExtractionConfig = {
      enabled,
      fileLabels: enabled ? fileLabels : [],
      extractionPrompt: enabled ? extractionPrompt.trim() : '',
    };

    setIsSubmitting(true);
    try {
      await onSave(config);
      setOpen(false);
    } catch (error) {
      // Error is handled in the hook
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {trigger || (
          <Button variant="ghost" size="icon" disabled={isDisabled}>
            <GearSix size={16} />
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent className="w-[400px]" align="end">
        <div className="space-y-4">
          <div>
            <h4 className="font-medium text-sm mb-1">AI Extractie Configuratie</h4>
            <p className="text-xs text-muted-foreground">
              Configureer automatische waarde-extractie uit bestanden
            </p>
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="ai-enabled" className="text-sm">
              AI extractie inschakelen
            </Label>
            <Switch
              id="ai-enabled"
              checked={enabled}
              onCheckedChange={setEnabled}
              disabled={isSubmitting}
            />
          </div>

          {enabled && (
            <>
              <div className="space-y-2">
                <Label htmlFor="file-labels" className="text-sm">
                  Bestandslabels
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="file-labels"
                    value={fileLabelsInput}
                    onChange={(e) => setFileLabelsInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Bijv. energielabel, EPC"
                    disabled={isSubmitting}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleAddLabel}
                    disabled={isSubmitting || !fileLabelsInput.trim()}
                  >
                    Toevoegen
                  </Button>
                </div>
                {fileLabels.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {fileLabels.map((label) => (
                      <Badge
                        key={label}
                        variant="secondary"
                        className="gap-1 cursor-pointer"
                        onClick={() => !isSubmitting && handleRemoveLabel(label)}
                      >
                        {label}
                        <X size={12} weight="bold" />
                      </Badge>
                    ))}
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  Alleen bestanden met deze labels worden gebruikt voor extractie
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="extraction-prompt" className="text-sm">
                  Extractie prompt
                </Label>
                <Textarea
                  id="extraction-prompt"
                  value={extractionPrompt}
                  onChange={(e) => setExtractionPrompt(e.target.value)}
                  placeholder="Beschrijf wat er uit het bestand geëxtraheerd moet worden..."
                  rows={4}
                  disabled={isSubmitting}
                  className="text-sm"
                />
                <p className="text-xs text-muted-foreground">
                  Geef duidelijke instructies voor wat geëxtraheerd moet worden
                </p>
              </div>
            </>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOpen(false)}
              disabled={isSubmitting}
            >
              Annuleren
            </Button>
            <Button size="sm" onClick={handleSave} disabled={isSubmitting}>
              {isSubmitting ? 'Bezig met opslaan...' : 'Opslaan'}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default AIExtractionConfigPopover;
